// Team plan access in the web app (docs/team/CONTRACT.md sections 3, 4 and 10).
// The owner's account is the business. A staff member signs in with their own
// login and works inside the owner's business; /auth/me then returns
// user.team with is_staff_context: true and the permissions the owner chose.
// Solo accounts have user.team === null and every helper here leaves them
// exactly as before (everything allowed, no team controls).
import { isTeamPlanCode } from "./featureAccess.js";

export const TEAM_ROLE_OWNER = "team_owner";
export const TEAM_ROLE_STAFF = "team_staff";

// Section 3 of the contract. The API serves the same catalogue
// (GET /team permission_catalogue); this copy is the fallback and the source
// of the plain words staff see about themselves.
export const TEAM_PERMISSION_CATALOGUE = Object.freeze([
  { key: "appointments_view_all", label: "See the whole team's appointments (off: only their own)", group: "Appointments" },
  { key: "appointments_manage", label: "Book, move and cancel appointments they can see", group: "Appointments" },
  { key: "appointments_assign", label: "Choose who does an appointment (needs the two above)", group: "Appointments" },
  { key: "clients_view_all", label: "See every client (off: only clients they have appointments with)", group: "Clients" },
  { key: "clients_contact_details", label: "See client phone numbers and email addresses", group: "Clients" },
  { key: "clients_manage", label: "Add and edit clients", group: "Clients" },
  { key: "payments_take", label: "Take payments and mark their appointments as paid", group: "Money" },
  { key: "payments_refund", label: "Give refunds", group: "Money" },
  { key: "invoices_manage", label: "Create and send invoices", group: "Money" },
  { key: "finance_view", label: "See the business's payments, bookkeeping and reports", group: "Money" },
  { key: "services_manage", label: "Change services and prices", group: "Business" },
  { key: "booking_settings_manage", label: "Change online booking settings", group: "Business" },
  { key: "reminders_manage", label: "Change reminders and customer messages", group: "Business" },
  { key: "ai_receptionist_view", label: "See AI receptionist calls and messages", group: "Business" },
  { key: "leads_view", label: "See and answer customer leads", group: "Business" },
  { key: "stock_manage", label: "Manage stock", group: "Business" },
  { key: "forms_manage", label: "Change form and document templates", group: "Business" },
].map((entry) => Object.freeze(entry)));

export const TEAM_PERMISSION_KEYS = Object.freeze(TEAM_PERMISSION_CATALOGUE.map((entry) => entry.key));
export const TEAM_PERMISSION_GROUPS = Object.freeze(["Appointments", "Clients", "Money", "Business"]);

// A permission that only works together with others (enforced on save).
// Calls, messages and leads exist to ring or email the customer back, so they
// come with the client contact details.
export const TEAM_PERMISSION_DEPENDENCIES = Object.freeze({
  appointments_assign: Object.freeze(["appointments_view_all", "appointments_manage"]),
  payments_refund: Object.freeze(["payments_take"]),
  ai_receptionist_view: Object.freeze(["clients_contact_details"]),
  leads_view: Object.freeze(["clients_contact_details"]),
});

const FIELD_STAFF = ["appointments_manage", "clients_contact_details", "payments_take"];

export const TEAM_PRESETS = Object.freeze([
  {
    key: "field_staff",
    label: "Field staff",
    description: "Their own appointments: book and move them, see client phone numbers and take payments.",
    permissions: FIELD_STAFF,
  },
  {
    key: "senior_staff",
    label: "Senior staff",
    description: "Field staff, plus the whole team's diary, choosing who does what, every client and invoices.",
    permissions: [
      ...FIELD_STAFF,
      "appointments_view_all",
      "appointments_assign",
      "clients_view_all",
      "clients_manage",
      "invoices_manage",
    ],
  },
  {
    key: "office_admin",
    label: "Office manager",
    description: "Everything except your plan, billing and the team itself.",
    permissions: [...TEAM_PERMISSION_KEYS],
  },
  {
    key: "diary_only",
    label: "Diary only",
    description: "Only their own appointments: update the status and add notes.",
    permissions: [],
  },
].map((preset) => Object.freeze({ ...preset, permissions: Object.freeze([...preset.permissions]) })));

export const DEFAULT_INVITE_PRESET = "field_staff";
export const CUSTOM_PRESET = "custom";

// Nav sections a staff member may open (contract section 10). Anything not
// listed is closed to staff: deny by default, like the API's route guard.
export const STAFF_SECTION_RULES = Object.freeze({
  dashboard: "always",
  calendar: "always",
  timeline: "always",
  clients: "always",
  services: "always",
  appointment_notes: "always",
  documents: "always",
  tasks: "always",
  team: "always",
  account: "always",
  stock: "stock_manage",
  booking: "booking_settings_manage",
  ai_receptionist: "ai_receptionist_view",
  leads: "leads_view",
  growth: "leads_view",
  payments: "finance_view",
  bookkeeping: "finance_view",
  reports: "finance_view",
  notifications: "reminders_manage",
  credits: "never",
  activity: "never",
});

function teamOf(user) {
  const team = user?.team;
  return team && typeof team === "object" ? team : null;
}

export function getTeamContext(user) {
  const team = teamOf(user);
  if (!team) return null;
  const role = String(team.role || "").trim().toLowerCase();
  const isStaff = isStaffContext(user);
  const membershipId = Number(team.membership_id);
  const teamId = Number(team.team_id);
  return {
    teamId: Number.isFinite(teamId) ? teamId : null,
    teamName: String(team.team_name || team.business_name || "").trim(),
    businessName: String(team.business_name || team.team_name || "").trim(),
    role: role || (isStaff ? TEAM_ROLE_STAFF : TEAM_ROLE_OWNER),
    membershipId: Number.isFinite(membershipId) && membershipId > 0 ? membershipId : null,
    isStaffContext: isStaff,
    displayName: String(team.display_name || "").trim(),
    colour: String(team.colour || "").trim(),
    ownerName: String(team.owner_name || "").trim(),
    permissions: isStaff ? normalizePermissions(team.permissions) : allPermissions(),
  };
}

// True while a staff member is working inside someone else's business.
export function isStaffContext(user) {
  const team = teamOf(user);
  if (!team) return false;
  if (team.is_staff_context === true) return true;
  if (team.is_staff_context === false) return false;
  return String(team.role || "").trim().toLowerCase() === TEAM_ROLE_STAFF;
}

// The business owner on a Team plan (their team may not exist yet: the API
// creates it on the first GET /team).
export function isTeamOwner(user) {
  if (!user || isStaffContext(user)) return false;
  const team = teamOf(user);
  if (team && String(team.role || "").trim().toLowerCase() === TEAM_ROLE_OWNER) return true;
  return isTeamPlanCode(user.plan) || isTeamPlanCode(user.subscription_plan);
}

// Owners and staff see the Team section; solo accounts never do.
export function isTeamAccount(user) {
  return isStaffContext(user) || isTeamOwner(user);
}

// Owners and solo accounts can do everything; staff only what they were given.
export function hasTeamPermission(user, key) {
  if (!isStaffContext(user)) return true;
  const permissions = teamOf(user)?.permissions;
  return Boolean(permissions && typeof permissions === "object" && permissions[key] === true);
}

export function staffAllowsSection(sectionKey, user) {
  if (!isStaffContext(user)) return true;
  const rule = STAFF_SECTION_RULES[String(sectionKey || "")];
  if (!rule || rule === "never") return false;
  if (rule === "always") return true;
  return hasTeamPermission(user, rule);
}

export function teamBusinessName(user) {
  const team = teamOf(user);
  return String(team?.business_name || team?.team_name || "").trim();
}

// The account menu's line for staff: "Bright Feet · Staff".
export function staffAccountLabel(user) {
  if (!isStaffContext(user)) return "";
  const name = teamBusinessName(user);
  return name ? name + " · Staff" : "Staff";
}

// Key for caches and remembered choices that belong to one account. A staff
// member inside a business gets a key of their own (their own account and the
// team never share cached data); everyone else keeps their user id.
export function teamAccountCacheKey(user) {
  if (!user || user.id == null || user.id === "") return "";
  const team = teamOf(user);
  if (isStaffContext(user) && team?.team_id != null) return String(user.id) + "~team-" + String(team.team_id);
  return String(user.id);
}

export function allPermissions() {
  return Object.fromEntries(TEAM_PERMISSION_KEYS.map((key) => [key, true]));
}

export function normalizePermissions(value) {
  const source = value && typeof value === "object" ? value : {};
  const list = Array.isArray(value) ? value : null;
  return Object.fromEntries(TEAM_PERMISSION_KEYS.map((key) => [
    key,
    list ? list.includes(key) : source[key] === true,
  ]));
}

export function permissionsForPreset(presetKey, presets = TEAM_PRESETS) {
  const preset = (Array.isArray(presets) && presets.length ? presets : TEAM_PRESETS)
    .find((entry) => entry?.key === presetKey)
    || TEAM_PRESETS.find((entry) => entry.key === presetKey);
  return normalizePermissions(preset ? preset.permissions : []);
}

// The preset whose permissions are exactly these, else "custom".
export function presetForPermissions(permissions, presets = TEAM_PRESETS) {
  const normalized = normalizePermissions(permissions);
  const list = Array.isArray(presets) && presets.length ? presets : TEAM_PRESETS;
  const match = list.find((preset) => {
    const presetPermissions = permissionsForPreset(preset?.key, list);
    return TEAM_PERMISSION_KEYS.every((key) => presetPermissions[key] === normalized[key]);
  });
  return match ? match.key : CUSTOM_PRESET;
}

export function presetLabel(presetKey, presets = TEAM_PRESETS) {
  if (presetKey === "owner") return "Owner";
  if (presetKey === CUSTOM_PRESET) return "Custom";
  const list = Array.isArray(presets) && presets.length ? presets : TEAM_PRESETS;
  return (list.find((preset) => preset?.key === presetKey) || TEAM_PRESETS.find((preset) => preset.key === presetKey))?.label
    || "Custom";
}

// Turning one permission on or off, with the dependency rules applied:
// on -> what it needs comes on too; off -> what needs it goes off too.
export function setTeamPermission(permissions, key, enabled) {
  const next = normalizePermissions(permissions);
  if (!TEAM_PERMISSION_KEYS.includes(key)) return next;
  next[key] = Boolean(enabled);
  if (enabled) {
    (TEAM_PERMISSION_DEPENDENCIES[key] || []).forEach((required) => {
      next[required] = true;
    });
  } else {
    Object.entries(TEAM_PERMISSION_DEPENDENCIES).forEach(([dependent, required]) => {
      if (required.includes(key)) next[dependent] = false;
    });
  }
  return next;
}

// Drops any permission whose requirements are missing (the API refuses them).
export function enforcePermissionDependencies(permissions) {
  const next = normalizePermissions(permissions);
  Object.entries(TEAM_PERMISSION_DEPENDENCIES).forEach(([dependent, required]) => {
    if (next[dependent] && required.some((key) => !next[key])) next[dependent] = false;
  });
  return next;
}

export function permissionDependencyNote(key) {
  if (key === "appointments_assign") {
    return "Needs \"See the whole team's appointments\" and \"Book, move and cancel appointments\".";
  }
  if (key === "payments_refund") return "Needs \"Take payments\".";
  if (key === "ai_receptionist_view" || key === "leads_view") {
    return "Needs \"See client phone numbers and email addresses\".";
  }
  return "";
}

// Plain words for staff about what they can do (Team page, staff view).
const STAFF_ABILITY_WORDS = Object.freeze({
  appointments_view_all: "See the whole team's appointments",
  appointments_manage: "Book, move and cancel appointments",
  appointments_assign: "Choose who does each appointment",
  clients_view_all: "See every client",
  clients_contact_details: "See client phone numbers and email addresses",
  clients_manage: "Add and edit clients",
  payments_take: "Take payments and mark your appointments as paid",
  payments_refund: "Give refunds",
  invoices_manage: "Create and send invoices",
  finance_view: "See the business's payments, bookkeeping and reports",
  services_manage: "Change services and prices",
  booking_settings_manage: "Change the online booking settings",
  reminders_manage: "Change reminders and customer messages",
  ai_receptionist_view: "See AI receptionist calls and messages",
  leads_view: "See and answer customer leads",
  stock_manage: "Manage stock",
  forms_manage: "Change form and document templates",
});

export const STAFF_ALWAYS_ALLOWED = Object.freeze([
  "See your own appointments and the clients on them, with their addresses",
  "Mark your appointments as on the way, started, completed or no-show",
  "Add notes, photos and forms to your appointments",
  "See the services list and your tasks",
]);

export function staffAbilities(permissions) {
  const normalized = normalizePermissions(permissions);
  const can = [...STAFF_ALWAYS_ALLOWED];
  const cannot = [];
  TEAM_PERMISSION_KEYS.forEach((key) => {
    const words = STAFF_ABILITY_WORDS[key];
    if (!words) return;
    if (normalized[key]) can.push(words);
    else cannot.push(words);
  });
  return { can, cannot };
}

export function teamPermissionsForUser(user) {
  if (!isStaffContext(user)) return allPermissions();
  return normalizePermissions(teamOf(user)?.permissions);
}

// Staff can switch between the team they work for and their own Offlico
// account (POST /team/workspace). /auth/me only describes the workspace in use,
// so the team is remembered per login in this browser to offer the way back.
const WORKSPACE_MEMORY_PREFIX = "offlico.team.workspace.";

function workspaceStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function rememberTeamWorkspace(user) {
  const storage = workspaceStorage();
  const team = teamOf(user);
  if (!storage || !isStaffContext(user) || user?.id == null || team?.team_id == null) return;
  try {
    storage.setItem(WORKSPACE_MEMORY_PREFIX + user.id, JSON.stringify({
      team_id: Number(team.team_id),
      business_name: String(team.business_name || team.team_name || "").trim(),
    }));
  } catch {
    // Best effort.
  }
}

export function readRememberedTeamWorkspace(user) {
  const storage = workspaceStorage();
  if (!storage || user?.id == null || isStaffContext(user)) return null;
  try {
    const parsed = JSON.parse(storage.getItem(WORKSPACE_MEMORY_PREFIX + user.id) || "null");
    const teamId = Number(parsed?.team_id);
    if (!Number.isInteger(teamId) || teamId <= 0) return null;
    if (teamOf(user)?.team_id != null && Number(teamOf(user).team_id) === teamId) return null;
    return { teamId, businessName: String(parsed?.business_name || "").trim() || "your team" };
  } catch {
    return null;
  }
}

export function forgetTeamWorkspace(user) {
  const storage = workspaceStorage();
  if (!storage || user?.id == null) return;
  try {
    storage.removeItem(WORKSPACE_MEMORY_PREFIX + user.id);
  } catch {
    // Best effort.
  }
}
